import { Injectable, inject } from '@angular/core';

import { map, Observable } from 'rxjs';

import { ApiService } from '@services/api/api.service';
import {
  OperationModel,
  OperationTransactionModel,
  PendingEventCreationModel,
  PendingOperationsModel,
  CancelOperationResponseModel,
  FinishedProccessEventsModel,
  ReceiptDataModel,
} from '@components/operations/operation.model';
import { PaginationConfig } from '@magis-ui/magis-ui';
import {
  AddJournalOperationBodyModel,
  JournalBodyModel,
  JournalModel,
} from '@components/operations/journal/journal.models';
import {
  SlipDetailModel,
  SlipTaskModel,
  SlipUsageDetailModel,
} from '@components/operations/counters/slips/slip.models';
import { BaseResponseDataModel, BaseListModel } from '@magis-ui/magis-ui';
import { OPeratorLevelModel } from '@components/admin/operator/operator.models';
import {
  AssignOperatorCodificationBodyModel,
  AssignRoleCodificationBodyModel,
  NewOperationTypeBodyModel,
  OperationCodeCodificationDetailModel,
  PaymentSchemaRequestBodyModel,
  PendingOperationTypeDetailModel,
} from '@components/operations/codification/codification.model';
import {
  NotificationCategoryBodyModel,
  NotificationCategoryModel,
  NotificationTypeBodyModel,
} from '@components/dev/notifications/notification.model';
import { DbsStatisticsResponseModel } from '@components/dashboards/dbs-dashboard/dbs-dashboard.models';
import {
  DailyFlowBreakdownModel,
  FlowAnalyticsResponseModel,
  HealthSummaryTreasuryDashboardModel,
  IntegrityStatusModel,
  KpiSummaryResponseModel,
  SparklinesModel,
  TreasuryBranchesResponseModel,
  TreasuryDashboardResponseModel,
} from '@components/dashboards/treasury-dashboard/treasury-dashboard.model';
import {
  InstitutionTreasuryAccountDetailModel,
  TreasuryMappingActionModel,
} from '@components/operations/institution-reconciliation/institution-reconciliations.models';

@Injectable({
  providedIn: 'root',
})
export class OperationsService {
  private apiService = inject(ApiService);

  getPendingOperations(
    pagination: PaginationConfig,
    search = '',
    filters = ''
  ): Observable<{
    objects: PendingOperationsModel[];
    count: number;
  }> {
    const url = `/hr/process/generic_pending/list/?generic_operation=true&limit=${pagination.filters.limit}&offset=${pagination.filters.offset}&search=${search}&${filters}`;
    return this.apiService
      .get<{ objects: PendingOperationsModel[]; count: number }>(url)
      .pipe(map(data => data));
  }

  performProcessEvent(
    operationId: number,
    body: {
      pin_code: string;
      action_event?: string;
      action_type: string;
    }
  ): Observable<BaseResponseDataModel<PendingEventCreationModel>> {
    const url = `/corporate/operation/transfer/${operationId}/perform_object_action/`;
    return this.apiService
      .post<BaseResponseDataModel<PendingEventCreationModel>>(url, body)
      .pipe(map(data => data));
  }

  getOperationsList(
    search: string,
    pagination: PaginationConfig,
    type: string,
    filtersQuery = ''
  ): Observable<BaseListModel<OperationModel[]>> {
    const url = `/dsl/operations/pending/logic/?operation_type=${type}&search=${search}&limit=${pagination.filters.limit}&offset=${pagination.filters.offset}${filtersQuery}`;

    return this.apiService
      .get<BaseListModel<OperationModel[]>>(url)
      .pipe(map(data => data));
  }

  getOPeratoLevel(): Observable<{ object: OPeratorLevelModel }> {
    const url = '/operations/reconcile/levels/';
    return this.apiService
      .get<{ object: OPeratorLevelModel }>(url)
      .pipe(map(data => data));
  }

  exturnOperation(
    operationId: number,
    password: string
  ): Observable<BaseResponseDataModel<CancelOperationResponseModel>> {
    const url = `/operations/${operationId}/exturn/`;
    return this.apiService
      .post<
        BaseResponseDataModel<CancelOperationResponseModel>
      >(url, { password: password })
      .pipe(map(data => data));
  }

  processOperation(
    operationId: number,
    password: string,
    external_reference: string,
    action: string
  ): Observable<BaseResponseDataModel<CancelOperationResponseModel>> {
    const url = `/operations/process/${operationId}/perform_object_action/`;
    return this.apiService
      .post<
        BaseResponseDataModel<CancelOperationResponseModel>
      >(url, { action_type: action, password: password, external_reference: external_reference })
      .pipe(map(data => data));
  }

  createNewSlip(body: {
    number_of_slips: number;
    branch: number;
    password: string;
  }): Observable<BaseResponseDataModel<null>> {
    const url = '/checks/slip-batches/';
    return this.apiService.post<BaseResponseDataModel<null>>(url, body).pipe(
      map(response => {
        return response;
      })
    );
  }

  submitSlipPrint(
    slipId: number,
    body: {
      password: string;
    }
  ): Observable<BaseResponseDataModel<{ task_id: string }>> {
    const url = `/checks/slip_batches/${slipId}/print1/`;
    return this.apiService
      .post<BaseResponseDataModel<{ task_id: string }>>(url, body)
      .pipe(map(response => response));
  }

  getSlipPrintTasks(
    pagination: PaginationConfig
  ): Observable<
    BaseResponseDataModel<{ count: number; results: SlipTaskModel[] }>
  > {
    const url = `/checks/slip-print/tasks/?limit=${pagination.filters.limit}&offset=${pagination.filters.offset}`;
    return this.apiService
      .get<
        BaseResponseDataModel<{ count: number; results: SlipTaskModel[] }>
      >(url)
      .pipe(map(response => response));
  }

  getSlipDetails(slipId: number): Observable<{ object: SlipDetailModel }> {
    const url = `/checks/slip-batches/${slipId}/`;
    return this.apiService
      .get<{ object: SlipDetailModel }>(url)
      .pipe(map(data => data));
  }

  getSlipUsageDetails(
    slipUsageId: string
  ): Observable<{ object: SlipUsageDetailModel }> {
    const url = `/checks/slip-usages/${slipUsageId}/`;
    return this.apiService
      .get<{ object: SlipUsageDetailModel }>(url)
      .pipe(map(data => data));
  }

  getReconciliationList(
    search = '',
    reconciliationFilter: string,
    pagination: PaginationConfig
  ): Observable<BaseListModel<OperationModel[]>> {
    const url = `/operations/pending/reconcile/?search=${search}&limit=${pagination.filters.limit}&offset=${pagination.filters.offset}&${reconciliationFilter}`;
    return this.apiService
      .get<BaseListModel<OperationModel[]>>(url)
      .pipe(map(data => data));
  }

  getReceiptData(
    operationId: number
  ): Observable<BaseResponseDataModel<ReceiptDataModel>> {
    const url = `/operations/pending/${operationId}/receipt/`;
    return this.apiService
      .get<BaseResponseDataModel<ReceiptDataModel>>(url)
      .pipe(map(data => data));
  }

  getReconciliatedOperations(
    search = '',
    pagination: PaginationConfig,
    otherFilterQuery: string
  ): Observable<BaseListModel<OperationModel[]>> {
    const url = `/operations/pending/reconciled/?search=${search}&limit=${pagination.filters.limit}&offset=${pagination.filters.offset}&${otherFilterQuery}`;
    return this.apiService
      .get<BaseListModel<OperationModel[]>>(url)
      .pipe(map(data => data));
  }

  // TODO :: FIX RETURN TYPE
  performReconciliationAction(
    body: { action_type: string; password: string },
    operation_id: number,
    reconciliationFilterQuery: string
  ): Observable<BaseResponseDataModel<object>> {
    const url = `/operations/pending/reconcile/${operation_id}/perform_object_action/?${reconciliationFilterQuery}`;
    return this.apiService
      .post<BaseResponseDataModel<object>>(url, body)
      .pipe(map(data => data));
  }

  getFinishedProccessEvent(
    processAction: number
  ): Observable<BaseListModel<FinishedProccessEventsModel[]>> {
    const url = `/hr/process/action_event/list/?hr_action=${processAction}`;
    return this.apiService
      .get<BaseListModel<FinishedProccessEventsModel[]>>(url)
      .pipe(map(data => data));
  }

  getOperationTransactions(
    operationId: number,
    pagination: PaginationConfig
  ): Observable<{
    objects: OperationTransactionModel[];
    count: number;
  }> {
    const url = `/operations/all/statement/?trans_pending_operation=${operationId}&limit=${pagination?.filters.limit}&offset=${pagination?.filters.offset}`;
    return this.apiService
      .get<{
        objects: OperationTransactionModel[];
        count: number;
      }>(url)
      .pipe(map(data => data));
  }

  getJournalList(
    search: string,
    pagination?: PaginationConfig,
    filterby?: 'ALL'
  ): Observable<BaseListModel<JournalModel[]>> {
    let url = `/operations/journals/list/?limit=${pagination?.filters.limit}&offset=${pagination?.filters.offset}&search=${search}&`;
    if (filterby) {
      url += 'get_all=true&';
    }
    return this.apiService
      .get<BaseListModel<JournalModel[]>>(url)
      .pipe(map(data => data));
  }

  getJournalTransactions(operation_id: number): Observable<{
    object: {
      description: string;
      operation_code: {
        id: number;
        code: string;
        description: string;
      };
      objects: OperationTransactionModel[];
      count: number;
    };
  }> {
    const url = `/journals/list/?operation_id=${operation_id}`;
    return this.apiService
      .get<{
        object: {
          description: string;
          operation_code: {
            id: number;
            code: string;
            description: string;
          };
          objects: OperationTransactionModel[];
          count: number;
        };
      }>(url)
      .pipe(map(data => data));
  }

  getJournalOperationDetails(
    operationId: number
  ): Observable<{ object: JournalModel }> {
    const url = `/operations/journals/list/${operationId}/`;
    return this.apiService
      .get<{ object: JournalModel }>(url)
      .pipe(map(data => data));
  }

  addJournalOperation(
    body: AddJournalOperationBodyModel
  ): Observable<BaseResponseDataModel<{ id: number; detail: string }>> {
    const url = '/operations/add-journals/';
    return this.apiService
      .post<BaseResponseDataModel<{ id: number; detail: string }>>(url, body)
      .pipe(map(data => data));
  }

  addTransactionsToAJournal(
    body: {
      journals: JournalBodyModel[];
      password: string;
      operation_code?: string;
      description?: string;
    },
    operation_id: number
  ): Observable<BaseResponseDataModel<null>> {
    const url = `/operations/${operation_id}/add-items/`;
    return this.apiService
      .post<BaseResponseDataModel<null>>(url, body)
      .pipe(map(data => data));
  }

  removeTransactionsToAJournal(
    body: { journals: number[]; password: string },
    operation_id: number
  ): Observable<BaseResponseDataModel<null>> {
    const url = `/operations/${operation_id}/remove-items/`;
    return this.apiService
      .post<BaseResponseDataModel<null>>(url, body)
      .pipe(map(data => data));
  }

  verifyJournal(
    body: { password: string },
    operation_id: number
  ): Observable<BaseResponseDataModel<{ id: number; detail: string }>> {
    const url = `/operations/verify-operation/?operation_id=${operation_id}`;
    return this.apiService
      .post<BaseResponseDataModel<{ id: number; detail: string }>>(url, body)
      .pipe(map(data => data));
  }

  completeJournal(
    body: { password: string },
    operation_id: number
  ): Observable<BaseResponseDataModel<{ id: number; detail: string }>> {
    const url = `/operations/${operation_id}/confirm-journal/`;
    return this.apiService
      .post<BaseResponseDataModel<{ id: number; detail: string }>>(url, body)
      .pipe(map(data => data));
  }

  deleteJournal(
    operation_id: number,
    body: { password: string }
  ): Observable<BaseResponseDataModel<null>> {
    const url = `/operations/` + operation_id + `/remove-operation/`;
    return this.apiService
      .post<BaseResponseDataModel<null>>(url, body)
      .pipe(map(data => data));
  }

  getOperationCodificationDetails(
    transactionId: number
  ): Observable<{ object: OperationCodeCodificationDetailModel }> {
    const url = `/operations/codification/${transactionId}/`;
    return this.apiService
      .get<{ object: OperationCodeCodificationDetailModel }>(url)
      .pipe(map(data => data));
  }

  // deactivateTransactionType(
  //   transactionId: number,
  //   body: { password: string }
  // ): Observable<BaseResponseDataModel<object>> {
  //   const url = `/operations/codification/${transactionId}/deactivate/`;
  //   return this.apiService
  //     .post<BaseResponseDataModel<object>>(url, body)
  //     .pipe(map(data => data));
  // }
  getTransactionTypes(
    pagination: PaginationConfig
  ): Observable<BaseListModel<OperationCodeCodificationDetailModel[]>> {
    const url = `/operations/codification/?limit=${pagination.filters.limit}&offset=${pagination.filters.offset}&`;
    return this.apiService
      .get<BaseListModel<OperationCodeCodificationDetailModel[]>>(url)
      .pipe(map(data => data));
  }

  createSchemaForOperation(
    body: NewOperationTypeBodyModel
  ): Observable<BaseResponseDataModel<object>> {
    const url = `/operations/codification/`;
    return this.apiService
      .post<BaseResponseDataModel<object>>(url, body)
      .pipe(map(data => data));
  }

  paymentSchemaForOperation(
    body: PaymentSchemaRequestBodyModel
  ): Observable<BaseResponseDataModel<object>> {
    const url = `/operations/schema-payment/`;
    return this.apiService
      .post<BaseResponseDataModel<object>>(url, body)
      .pipe(map(data => data));
  }

  addPaymentJournalsFromSchema(body: {
    schema_id: number;
    password: string;
  }): Observable<BaseResponseDataModel<object>> {
    const url = `/operations/add-journals-from-schema/`;
    return this.apiService
      .post<BaseResponseDataModel<object>>(url, body)
      .pipe(map(data => data));
  }
  getJournalCodification(
    schemaId: number,
    pagination?: PaginationConfig
  ): Observable<BaseListModel<JournalModel[]>> {
    const url = `/operations/journals/list/?schema=${schemaId}&limit=${pagination?.filters.limit}&offset=${pagination?.filters.offset}&`;
    return this.apiService
      .get<BaseListModel<JournalModel[]>>(url)
      .pipe(map(data => data));
  }

  addTransactionsCodificationToAJournal(
    operationId: number,
    body: {
      schema_id: number;
      password: string;
    }
  ): Observable<BaseResponseDataModel<object>> {
    const url = `/operations/${operationId}/add-items-from-schema/`;
    return this.apiService
      .patch<BaseResponseDataModel<object>>(url, body)
      .pipe(map(data => data));
  }

  addNotificationCategory(
    body: NotificationCategoryBodyModel
  ): Observable<BaseResponseDataModel<object>> {
    const url = `/comm/notif-categ/`;
    return this.apiService
      .post<BaseResponseDataModel<object>>(url, body)
      .pipe(map(data => data));
  }

  getNotificationCategoryDetails(
    categoryId: number
  ): Observable<{ object: NotificationCategoryModel }> {
    const url = `/comm/notif-categ/${categoryId}/`;
    return this.apiService
      .get<{ object: NotificationCategoryModel }>(url)
      .pipe(map(data => data));
  }

  updateNotificationCategory(
    categoryId: number,
    body: NotificationCategoryBodyModel
  ): Observable<BaseResponseDataModel<object>> {
    const url = `/comm/notif-categ/${categoryId}/`;
    return this.apiService
      .patch<BaseResponseDataModel<object>>(url, body)
      .pipe(map(data => data));
  }

  addNotificationType(
    body: NotificationTypeBodyModel
  ): Observable<BaseResponseDataModel<object>> {
    const url = `/comm/notif-domain/`;
    return this.apiService
      .post<BaseResponseDataModel<object>>(url, body)
      .pipe(map(data => data));
  }

  updateNotificationType(
    domainId: number,
    body: NotificationTypeBodyModel
  ): Observable<BaseResponseDataModel<object>> {
    const url = `/comm/notif-domain/${domainId}/`;
    return this.apiService
      .patch<BaseResponseDataModel<object>>(url, body)
      .pipe(map(data => data));
  }

  getDbsStatistics(
    filtersQuery = ''
  ): Observable<BaseResponseDataModel<DbsStatisticsResponseModel>> {
    const url = `/operations/dbs-statistics/?${filtersQuery}`;
    return this.apiService
      .get<BaseResponseDataModel<DbsStatisticsResponseModel>>(url)
      .pipe(map(data => data));
  }

  getTreasuryDashboard(
    branchId?: number | null,
    days?: number
  ): Observable<BaseResponseDataModel<TreasuryDashboardResponseModel>> {
    let url = '/treasury/dashboard/?';
    if (branchId) {
      url += `branch_id=${branchId}&`;
    }
    if (days) {
      url += `days=${days}&`;
    }
    return this.apiService
      .get<BaseResponseDataModel<TreasuryDashboardResponseModel>>(url)
      .pipe(map(data => data));
  }

  getTreasuryDashboardBranches(): Observable<{
    object: TreasuryBranchesResponseModel;
  }> {
    const url = '/treasury/dashboard/branches/';
    return this.apiService
      .get<{ object: TreasuryBranchesResponseModel }>(url)
      .pipe(map(data => data));
  }

  getTreasuryDashboardKpiSummary(
    branchId?: number | null
  ): Observable<{ object: KpiSummaryResponseModel }> {
    let url = '/treasury/dashboard/kpi-summary/?';
    if (branchId) {
      url += `branch_id=${branchId}&`;
    }
    return this.apiService
      .get<{ object: KpiSummaryResponseModel }>(url)
      .pipe(map(data => data));
  }

  getTreasuryDashboardSparklines(
    branchId?: number | null,
    days = 30
  ): Observable<BaseResponseDataModel<SparklinesModel>> {
    let url = `/treasury/dashboard/sparklines/?days=${days}&`;
    if (branchId) {
      url += `branch_id=${branchId}&`;
    }
    return this.apiService
      .get<BaseResponseDataModel<SparklinesModel>>(url)
      .pipe(map(data => data));
  }

  getTreasuryDashboardFlowAnalytics(
    days: number,
    branchId?: number | null
  ): Observable<{ object: FlowAnalyticsResponseModel }> {
    let url = `/treasury/dashboard/flow-analytics/?days=${days}&`;
    if (branchId) {
      url += `branch_id=${branchId}&`;
    }
    return this.apiService
      .get<{ object: FlowAnalyticsResponseModel }>(url)
      .pipe(map(data => data));
  }

  getTreasuryDashboardAuditIntegrityStatus(): Observable<{
    object: IntegrityStatusModel;
  }> {
    const url = '/treasury/dashboard/audit/integrity-status/';
    return this.apiService
      .get<{ object: IntegrityStatusModel }>(url)
      .pipe(map(data => data));
  }
  getTreasuryDashboardFlowBreakdown(
    branchId?: number | null
  ): Observable<{ object: DailyFlowBreakdownModel }> {
    let url = '/treasury/dashboard/flow-breakdown/?';
    if (branchId) {
      url += `branch_id=${branchId}&`;
    }
    return this.apiService
      .get<{ object: DailyFlowBreakdownModel }>(url)
      .pipe(map(data => data));
  }

  getTreasuryDashboardHealthSummary(
    branchId?: number | null
  ): Observable<{ object: HealthSummaryTreasuryDashboardModel }> {
    let url = '/treasury/dashboard/health-summary/?';
    if (branchId) {
      url += `branch_id=${branchId}&`;
    }
    return this.apiService
      .get<{ object: HealthSummaryTreasuryDashboardModel }>(url)
      .pipe(map(data => data));
  }

  assignSchemaProcedureStepAccess(
    body: AssignRoleCodificationBodyModel
  ): Observable<BaseResponseDataModel<object>> {
    const url = `/operations/schema-procedure-step-access/`;
    return this.apiService
      .post<BaseResponseDataModel<object>>(url, body)
      .pipe(map(data => data));
  }

  assignSchemaOperatorAccess(
    body: AssignOperatorCodificationBodyModel
  ): Observable<BaseResponseDataModel<object>> {
    const url = `/operations/schema-access/`;
    return this.apiService
      .post<BaseResponseDataModel<object>>(url, body)
      .pipe(map(data => data));
  }

  getPendingOperationDetails(
    operationId: number
  ): Observable<BaseResponseDataModel<PendingOperationTypeDetailModel>> {
    const url = `/operations/pending/schema-inbox/${operationId}/`;
    return this.apiService
      .get<BaseResponseDataModel<PendingOperationTypeDetailModel>>(url)
      .pipe(map(data => data));
  }

  getInstitutionReconciliationDetails(
    operationId: number
  ): Observable<{ object: InstitutionTreasuryAccountDetailModel }> {
    const url = `/treasury/institutions/reconciliation/${operationId}/`;
    return this.apiService
      .get<{ object: InstitutionTreasuryAccountDetailModel }>(url)
      .pipe(map(data => data));
  }

  confirmActionsInstitutionReconciliation(
    operationId: number,
    body: { action_type: string; ext_balance?: number; pin_code: string }
  ): Observable<BaseResponseDataModel<object>> {
    const url = `/treasury/institutions/reconciliation/${operationId}/perform_object_action/`;
    return this.apiService
      .post<BaseResponseDataModel<object>>(url, body)
      .pipe(map(data => data));
  }

  getTreasuryMappingActions(
    operationId: number
  ): Observable<BaseResponseDataModel<TreasuryMappingActionModel[]>> {
    const url = `/treasury/institutions/reconciliation/${operationId}/object_actions/`;
    return this.apiService
      .get<BaseResponseDataModel<TreasuryMappingActionModel[]>>(url)
      .pipe(map(data => data));
  }
}
